import React from 'react';
import {Reservation, User} from './types';

interface ReservationDetailModalProps {
  selectedReservation: Reservation | null;
  reservationDrafts: Record<string, Partial<Reservation>>;
  getStatusBadgeClass: (status: Reservation['status']) => string;
  updateReservationDraft: (
    id: string,
    key: keyof Reservation,
    value: string,
  ) => void;
  onClose: () => void;
  onSave: (id: string) => Promise<boolean>;
}

export const ReservationDetailModal: React.FC<ReservationDetailModalProps> = ({
  selectedReservation,
  reservationDrafts,
  getStatusBadgeClass,
  updateReservationDraft,
  onClose,
  onSave,
}) => {
  if (!selectedReservation) {
    return null;
  }

  return (
    <div
      className='reservation-modal-overlay'
      onClick={onClose}>
      <div
        className='reservation-modal'
        role='dialog'
        aria-modal='true'
        aria-label='Reservation details'
        onClick={(event) => event.stopPropagation()}>
        <div className='reservation-modal-header'>
          <h3>Reservation Details</h3>
          <button
            type='button'
            className='reservation-close'
            onClick={onClose}>
            Close
          </button>
        </div>

        <div className='reservation-static'>
          <p>
            <strong>Customer:</strong> {selectedReservation.customerName}
          </p>
          <p>
            <strong>Email:</strong> {selectedReservation.customerEmail}
          </p>
          <p>
            <strong>Phone:</strong>{' '}
            {selectedReservation.customerPhone || 'Not provided'}
          </p>
          <p>
            <strong>Status:</strong>{' '}
            <span className={getStatusBadgeClass(selectedReservation.status)}>
              {selectedReservation.status}
            </span>
          </p>
        </div>

        <div className='reservation-form-grid'>
          <label htmlFor='detail-date'>Date</label>
          <input
            id='detail-date'
            type='date'
            value={
              (reservationDrafts[selectedReservation.id]?.date as string) ||
              selectedReservation.date
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'date',
                e.target.value,
              )
            }
          />

          <label htmlFor='detail-time'>Time</label>
          <input
            id='detail-time'
            type='time'
            value={
              (reservationDrafts[selectedReservation.id]?.time as string) ||
              selectedReservation.time
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'time',
                e.target.value,
              )
            }
          />

          <label htmlFor='detail-guests'>Guests</label>
          <input
            id='detail-guests'
            type='number'
            min={1}
            value={String(
              (reservationDrafts[selectedReservation.id]
                ?.numberOfGuests as number) ||
                selectedReservation.numberOfGuests,
            )}
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'numberOfGuests',
                e.target.value,
              )
            }
          />

          <label htmlFor='detail-status'>Status</label>
          <select
            id='detail-status'
            value={
              (reservationDrafts[selectedReservation.id]?.status as string) ||
              selectedReservation.status
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'status',
                e.target.value,
              )
            }>
            <option value='booked'>booked</option>
            <option value='confirmed'>confirmed</option>
            <option value='cancelled'>cancelled</option>
          </select>

          <label htmlFor='detail-notes'>Notes</label>
          <textarea
            id='detail-notes'
            className='reservation-notes'
            value={
              (reservationDrafts[selectedReservation.id]?.notes as string) ||
              selectedReservation.notes ||
              ''
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'notes',
                e.target.value,
              )
            }
          />
        </div>

        <div className='reservation-modal-actions'>
          <button
            type='button'
            className='reservation-secondary'
            onClick={onClose}>
            Cancel
          </button>
          <button
            type='button'
            onClick={async () => {
              const saved = await onSave(selectedReservation.id);
              if (saved) {
                onClose();
              }
            }}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

interface UserDetailModalProps {
  selectedUser: User | null;
  onClose: () => void;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({
  selectedUser,
  onClose,
}) => {
  if (!selectedUser) {
    return null;
  }

  return (
    <div
      className='user-modal-overlay'
      onClick={onClose}>
      <div
        className='user-modal'
        role='dialog'
        aria-modal='true'
        aria-label='User details'
        onClick={(event) => event.stopPropagation()}>
        <div className='reservation-modal-header'>
          <h3>User Details</h3>
          <button
            type='button'
            className='reservation-close'
            onClick={onClose}>
            Close
          </button>
        </div>

        <div className='user-details-grid'>
          <div className='user-detail-item'>
            <p className='profile-label'>Name</p>
            <p>{selectedUser.name}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Email</p>
            <p>{selectedUser.email}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Role</p>
            <p>{selectedUser.role}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Status</p>
            <p>{selectedUser.isActive ? 'Active' : 'Inactive'}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Deleted</p>
            <p>{selectedUser.isDeleted ? 'Yes' : 'No'}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Last Login</p>
            <p>
              {selectedUser.lastLogin
                ? new Date(selectedUser.lastLogin).toLocaleString()
                : 'Not available'}
            </p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Last Login IP</p>
            <p>{selectedUser.lastLoginIp || 'Not available'}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Created At</p>
            <p>{new Date(selectedUser.createdAt).toLocaleString()}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Updated At</p>
            <p>{new Date(selectedUser.updatedAt).toLocaleString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
